import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { getMarketCoverage } from "@/lib/market-coverage.functions";
import { runMarketJob } from "@/lib/market-admin.functions";

export const Route = createFileRoute("/admin/prisdatabas")({
  head: () => ({
    meta: [
      { title: "Prisdatabas — täckning och insamling" },
      { name: "description", content: "Internt verktyg för prisdatabasens täckning per fabrikat." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: PriceDbAdmin,
});

const nf = new Intl.NumberFormat("sv-SE");

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-2 font-display text-3xl leading-none">{value}</p>
      {hint ? <p className="mt-2 text-xs text-muted-foreground">{hint}</p> : null}
    </div>
  );
}

function PriceDbAdmin() {
  const fetchCoverage = useServerFn(getMarketCoverage);
  const runJob = useServerFn(runMarketJob);
  const [log, setLog] = useState<string[]>([]);

  const coverage = useQuery({
    queryKey: ["market-coverage"],
    queryFn: () => fetchCoverage(),
    retry: false,
  });

  const job = useMutation({
    mutationFn: (vars: { mode: "brands" | "ingest" | "refresh"; offset?: number }) =>
      runJob({ data: { mode: vars.mode, offset: vars.offset, limit: 6 } }),
    onSuccess: (res, vars) => {
      setLog((l) => [`${vars.mode}: ${res.ok ? "ok" : "fel"} — ${res.detail}`, ...l].slice(0, 8));
      void coverage.refetch();
    },
    onError: (e: Error, vars) => setLog((l) => [`${vars.mode}: ${e.message}`, ...l].slice(0, 8)),
  });

  const d = coverage.data;

  return (
    <div className="container-edge py-16" data-ads="off">
      <p className="eyebrow">Internt</p>
      <h1 className="mt-3 font-display text-4xl">Prisdatabas</h1>
      <p className="mt-3 max-w-2xl text-sm text-muted-foreground">
        Underlaget bakom ankarpriserna i värderingen. Ett kluster måste ha minst tre annonser för
        att räknas som ankare, och exakta modellträffar väger tyngst.
      </p>

      <div className="mt-8 flex flex-wrap gap-3">
        {(
          [
            { mode: "brands" as const, label: "Crawla 6 fabrikat nu" },
            { mode: "ingest" as const, label: "Läs in bevakade annonser" },
            { mode: "refresh" as const, label: "Räkna om statistiken" },
          ]
        ).map((b) => (
          <button
            key={b.mode}
            type="button"
            disabled={job.isPending}
            onClick={() => job.mutate({ mode: b.mode, offset: 0 })}
            className="rounded-full border border-border bg-card px-5 py-2.5 text-sm font-medium transition hover:border-accent disabled:opacity-50"
          >
            {b.label}
          </button>
        ))}
      </div>

      {log.length ? (
        <pre className="mt-5 max-h-56 overflow-auto rounded-2xl border border-border bg-warm p-4 text-xs leading-relaxed whitespace-pre-wrap">
          {log.join("\n\n")}
        </pre>
      ) : null}

      {coverage.isLoading ? (
        <p className="mt-10 text-sm text-muted-foreground">Läser täckningen…</p>
      ) : coverage.isError ? (
        <p className="mt-10 text-sm text-muted-foreground">
          Kunde inte läsa täckningen. Sidan kräver admininloggning.
        </p>
      ) : d ? (
        <>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Stat label="Annonser" value={nf.format(d.total)} hint={`${nf.format(d.parsed)} tolkade`} />
            <Stat label="Med årsmodell" value={nf.format(d.withYear)} hint={`${nf.format(d.sold)} sålda`} />
            <Stat
              label="Ankare aktiva"
              value={nf.format(d.anchorsExact + d.anchorsBrandYear + d.anchorsBrand3)}
              hint={`${d.anchorsExact} modell · ${d.anchorsBrandYear} fabrikat · ${d.anchorsBrand3} 3-årsfönster`}
            />
            <Stat
              label="Senast omräknad"
              value={d.lastComputedAt ? new Date(d.lastComputedAt).toLocaleDateString("sv-SE") : "—"}
              hint={d.bySource.map((s) => `${s.source}: ${s.n}`).join(" · ") || "ingen källa"}
            />
          </div>

          <h2 className="mt-14 font-display text-2xl">Täckning per fabrikat</h2>
          <div className="mt-4 overflow-x-auto rounded-2xl border border-border">
            <table className="w-full min-w-[720px] text-sm">
              <thead className="bg-warm text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-4 py-3">Fabrikat</th>
                  <th className="px-4 py-3">Kategori</th>
                  <th className="px-4 py-3">Annonser</th>
                  <th className="px-4 py-3">Sålda</th>
                  <th className="px-4 py-3">Årsmodeller</th>
                  <th className="px-4 py-3">Medianpris</th>
                  <th className="px-4 py-3">Ankare</th>
                </tr>
              </thead>
              <tbody>
                {d.brands.map((b) => (
                  <tr key={`${b.category}-${b.brand}`} className="border-t border-border">
                    <td className="px-4 py-3 font-medium">{b.brand}</td>
                    <td className="px-4 py-3 text-muted-foreground">{b.category}</td>
                    <td className="px-4 py-3">{nf.format(b.n)}</td>
                    <td className="px-4 py-3">{nf.format(b.sold)}</td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {b.years} {b.minYear ? `(${b.minYear}–${b.maxYear})` : ""}
                    </td>
                    <td className="px-4 py-3">{b.medianPrice ? `${nf.format(b.medianPrice)} kr` : "—"}</td>
                    <td className={`px-4 py-3 ${b.anchors ? "text-accent" : "text-muted-foreground"}`}>
                      {b.anchors}
                    </td>
                  </tr>
                ))}
                {!d.brands.length ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-6 text-center text-muted-foreground">
                      Inga tolkade annonser ännu — kör insamlingen ovan.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </div>
  );
}
